import { useEffect, useState } from "react";

const navLinks = [
  { label: "首页", href: "#home" },
  { label: "漫画分类", href: "#categories" },
  { label: "今日更新", href: "#updates" },
  { label: "热门连载", href: "#hot" },
  { label: "脑洞专题", href: "#featured" },
  { label: "读者口碑", href: "#reviews" },
  { label: "常见问题", href: "#faq" },
  { label: "关于与联系", href: "#contact" },
];

interface NavbarProps {
  onSearch: (keyword: string) => void;
}

export default function Navbar({ onSearch }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    handler();
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const submitSearch = () => {
    onSearch(keyword);
    document.getElementById("updates")?.scrollIntoView({ behavior: "smooth" });
  };

  const textColor = scrolled ? "text-foreground-900" : "text-background-50";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background-50/95 backdrop-blur border-b border-background-200" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 md:h-[72px] max-w-7xl items-center justify-between px-4 md:px-6">
        <a href="#home" className="flex items-center gap-2 whitespace-nowrap">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-lg text-lg ${scrolled ? "bg-primary-500 text-background-50" : "bg-accent-500 text-foreground-950"}`}
          >
            <i className="ri-sparkling-2-fill"></i>
          </span>
          <span className={`font-heading text-xl md:text-2xl tracking-wide ${textColor}`}>
            歪歪漫画
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                scrolled
                  ? "text-foreground-700 hover:bg-background-100 hover:text-primary-600"
                  : "text-background-100 hover:bg-white/10"
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <div
            className={`hidden lg:flex items-center gap-2 rounded-full border px-3 py-2 transition-colors ${
              scrolled ? "border-background-200 bg-background-100" : "border-white/25 bg-white/10"
            }`}
          >
            <i className={`ri-search-line text-sm ${scrolled ? "text-foreground-600" : "text-background-100"}`}></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitSearch()}
              placeholder="搜漫画 / 标签"
              className={`w-32 bg-transparent text-sm outline-none placeholder:${scrolled ? "text-foreground-400" : "text-background-200"}`}
            />
            <button onClick={submitSearch} aria-label="搜索" className={`text-sm ${scrolled ? "text-primary-600" : "text-accent-300"}`}>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>

          <a
            href="#updates"
            className={`hidden sm:inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              scrolled ? "bg-primary-500 text-background-50 hover:bg-primary-600" : "bg-accent-500 text-foreground-950 hover:bg-accent-400"
            }`}
          >
            <i className="ri-play-fill"></i> 开始追更
          </a>

          <button
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="打开菜单"
            className={`md:hidden flex h-10 w-10 items-center justify-center rounded-md text-xl ${textColor}`}
          >
            <i className={menuOpen ? "ri-close-line" : "ri-menu-line"}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav className="md:hidden border-t border-background-200 bg-background-50 px-4 py-3">
          <div className="flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-3 py-2 mb-2">
            <i className="ri-search-line text-sm text-foreground-500"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setMenuOpen(false);
                  submitSearch();
                }
              }}
              placeholder="搜漫画 / 标签"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600"
              >
                {link.label}
              </a>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}